import { Modal } from "@material-ui/core";
import React from "react";
import styled, { keyframes } from "styled-components";
import theme from "../../../assets/theme";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import BundleService from "../../../services/BundleService";

const inputs = [
  {
    type: "number",
    name: "pageNo",
    label: "Page Number",
    required: true,
  },
  {
    type: "textarea",
    name: "annotationContent",
    label: "Annotation",
    required: true,
  },
];

const Annotation = ({ id, annotation, setAnnotation }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "Loading..." });
      const payload = {
        pageNo: values?.pageNo,
        annotationContent: values?.annotationContent,
      };
      const response = await BundleService.addAnnotationToBundles(id, payload);
      if (response) {
        enqueueSnackbar(response?.message, { variant: "success" });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setAnnotation(false);
    }
  }

  return (
    <Modal
      open={annotation}
      onClose={() => setAnnotation(false)}
      disableRestoreFocus={true}
    >
      <Overlay>
        <ModalCard>
          <ModalHeader>
            <HeaderText>
              <ModalTitle>Add Annotation</ModalTitle>
              <ModalSubtitle>
                Add a note to a specific page in the bundle
              </ModalSubtitle>
            </HeaderText>
            <CloseBtn onClick={() => setAnnotation(false)} aria-label="Close">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <ModalDivider />

          <Formik
            initialValues={{ pageNo: "", annotationContent: "" }}
            enableReinitialize
            validateOnBlur
            validateOnChange
            onSubmit={onFormSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              handleSubmit,
              touched,
              setFieldValue,
              setFieldError,
              handleBlur,
            }) => (
              <StyledForm onSubmit={handleSubmit}>
                <ModalBody>
                  <InfoBanner>
                    <InfoText>
                      Annotations help reviewers navigate important sections
                      quickly.
                    </InfoText>
                  </InfoBanner>

                  {inputs.map((input, index) => (
                    <FormField
                      key={index}
                      {...{
                        input,
                        values,
                        handleChange,
                        errors,
                        handleSubmit,
                        touched,
                        setFieldValue,
                        setFieldError,
                        handleBlur,
                      }}
                    />
                  ))}
                </ModalBody>

                <ModalDivider />

                <ModalFooter>
                  <PrimaryOutlinedCloseCTAButton
                    style={{ flex: 1 }}
                    onClick={(e) => {
                      e.preventDefault();
                      setAnnotation(false);
                    }}
                  >
                    Cancel
                  </PrimaryOutlinedCloseCTAButton>
                  <PrimaryCTAButton
                    style={{ flex: 1 }}
                    disabled={!values?.annotationContent || !values?.pageNo}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                  >
                    Add Annotation
                  </PrimaryCTAButton>
                </ModalFooter>
              </StyledForm>
            )}
          </Formik>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default Annotation;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -48%) scale(0.97); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  // background: rgba(15, 23, 42, 0.5);
  // backdrop-filter: blur(4px);
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 460px;
  background: #ffffff;
  border-radius: 20px;
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.18),
    0 8px 24px rgba(0, 131, 140, 0.1);
  animation: ${fadeSlideIn} 0.25s ease;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 24px 24px 20px;
`;

const HeaderText = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  color: #293461;
  line-height: 1.2;
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 0 24px;
`;

const StyledForm = styled.form``;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #f5f3ff;
  border: 1px solid #ede9fe;
  border-radius: 10px;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #7c3aed;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px 24px;
`;
